import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateShort(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
}

export function getPlatformColor(platform) {
  const colors = {
    airbnb: 'bg-[#ff5a5f] text-white',
    makemytrip: 'bg-[#eb2226] text-white',
    direct: 'bg-primary text-primary-foreground',
    ical: 'bg-rose-500 text-white',
  };
  return colors[platform?.toLowerCase()] || 'bg-slate-500 text-white';
}

export function getStatusColor(status) {
  const colors = {
    confirmed: 'bg-emerald-100 text-emerald-800',
    checked_in: 'bg-amber-100 text-amber-800',
    checked_out: 'bg-slate-100 text-slate-800',
    cancelled: 'bg-red-100 text-red-800',
    ical_pending: 'bg-orange-100 text-orange-800',
    payment_awaiting: 'bg-yellow-100 text-yellow-800',
  };
  return colors[status] || 'bg-slate-100 text-slate-800';
}

export function getStatusLabel(status) {
  const labels = {
    confirmed: 'Confirmed',
    checked_in: 'Checked In',
    checked_out: 'Checked Out',
    cancelled: 'Cancelled',
    ical_pending: 'iCal - Complete Details',
    payment_awaiting: 'Payment Awaiting',
  };
  return labels[status] || status;
}

export function getPlatformLabel(platform) {
  const labels = {
    airbnb: 'Airbnb',
    makemytrip: 'MakeMyTrip',
    direct: 'Direct',
    ical: 'iCal Import',
  };
  return labels[platform?.toLowerCase()] || platform;
}

export function calculateNights(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function isToday(dateString) {
  const today = new Date();
  const date = new Date(dateString);
  return date.toDateString() === today.toDateString();
}

export function isTomorrow(dateString) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const date = new Date(dateString);
  return date.toDateString() === tomorrow.toDateString();
}

// Resolve a displayable src for a guest ID / payment proof image.
// Handles R2 signed URLs, local previews and base64 (with or without a data: prefix).
export function resolveImageSrc(img) {
  if (!img) return null;
  if (img.previewUrl) return img.previewUrl;
  if (img.url) return img.url;
  const data = img.data;
  if (!data || typeof data !== 'string') return null;
  return data.startsWith('data:') ? data : `data:image/jpeg;base64,${data}`;
}


export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
}

// Compress and resize image to optimize storage
export function compressImage(file, maxWidth = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down if larger than maxWidth
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed JPEG
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
}

/**
 * Extract error message from API error response
 * Handles both Pydantic validation errors (array) and regular errors (string)
 */
export function getErrorMessage(error, fallback = 'An error occurred') {
  const detail = error?.response?.data?.detail;
  
  if (!detail) {
    return error?.message || fallback;
  }
  
  // Pydantic validation errors come as an array
  if (Array.isArray(detail)) {
    const messages = detail
      .map(err => err.msg || err.message || 'Validation error')
      .filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : fallback;
  }
  
  // Regular error message (string)
  if (typeof detail === 'string') {
    return detail;
  }
  
  return fallback;
}
